import { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useNewOnboardingEligibility } from '../../hooks/useNewOnboardingEligibility';
import { ROUTES_PATH } from '../../constants/Routes';
import { UserPreferences } from '../../services/userPreferences/types';
import { OnboardingToolsStatus } from './types';

export function useOnboardingTools() {
  const history = useHistory();
  const {
    baseEligible,
    completed: onboardingToolsCompleted,
    isSubscriptionLoading,
    isPollingCredits,
    flagResolved,
    isPreferencesLoading,
    preferences,
    updateUserPreferencesAsync,
  } = useNewOnboardingEligibility('onboardingToolsCompleted');

  const prefs = preferences as UserPreferences;
  const oabSkipped = Boolean(prefs.onboardingOabSkipped);
  const photoStepDone = Boolean(
    prefs.onboardingPhotoCompleted || prefs.onboardingProfileCompleted,
  );

  const status: OnboardingToolsStatus = useMemo(() => {
    if (isSubscriptionLoading || !flagResolved || isPreferencesLoading) {
      return 'loading';
    }

    if (!baseEligible || !oabSkipped) {
      return 'ineligible';
    }

    if (!photoStepDone) {
      return 'prior_pending';
    }

    if (onboardingToolsCompleted) {
      return 'completed';
    }

    return 'ready';
  }, [
    isSubscriptionLoading,
    flagResolved,
    isPreferencesLoading,
    baseEligible,
    oabSkipped,
    photoStepDone,
    onboardingToolsCompleted,
  ]);

  const selectTool = useCallback(
    async (route: string, onBeforeNavigate?: () => void) => {
      await updateUserPreferencesAsync({ onboardingToolsCompleted: true });
      onBeforeNavigate?.();
      history.push(route);
    },
    [updateUserPreferencesAsync, history],
  );

  const goBack = useCallback(() => {
    history.push(ROUTES_PATH.ONBOARDING.PHOTO);
  }, [history]);

  return { status, isPollingCredits, selectTool, goBack };
}
